import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@volca/ui/components/ui/card";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  openFindingCount,
  type useFindings,
} from "@/profitability/review/query";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { reportError } from "@/profitability/utils";

const PREVIEW = 3;

/** Counts describe the full queue; no totals are inferred from its first page. */
export const OverviewAttention = ({
  findings,
  reviewing,
  reviewPath,
  allPeriodsPath,
  empty,
  reviewAction,
}: {
  findings: ReturnType<typeof useFindings>;
  reviewing: boolean;
  reviewPath: string;
  allPeriodsPath?: string;
  empty: ReactNode;
  reviewAction: ReactNode;
}) => {
  const spec = useReviewSpec();
  const page = findings.data?.pages[0];
  const total = openFindingCount(page?.counts);
  const groups = [...(page?.rules ?? [])].sort(
    (a, b) => b.count - a.count || a.rule.localeCompare(b.rule),
  );
  const groupPath = (rule: string) => {
    const [path, search] = reviewPath.split("?");
    const params = new URLSearchParams(search);
    params.set("rule", rule);
    return `${path}?${params.toString()}`;
  };

  return (
    <Card
      className="min-w-0 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
      role="region"
      aria-label={spec.labels.review}
    >
      <CardHeader>
        <CardTitle>
          <h2>{spec.labels.review}</h2>
        </CardTitle>
        <CardDescription>
          {allPeriodsPath ? "This period" : "All periods"}
        </CardDescription>
        {allPeriodsPath && (
          <CardAction>
            <Button
              variant="ghost"
              size="sm"
              nativeButton={false}
              render={<Link to={allPeriodsPath} />}
            >
              All periods
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex min-w-0 flex-1 flex-col">
        {findings.isError ? (
          <ErrorState
            title="Findings could not load"
            description={reportError(findings.error)}
          >
            <Button onClick={() => void findings.refetch()}>Try again</Button>
          </ErrorState>
        ) : findings.isPending ? (
          <Stack gap={3} aria-busy aria-label="Loading review summary">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </Stack>
        ) : (
          <Stack gap={4}>
            {total === 0 && reviewing ? (
              <Text variant="body" tone="muted" role="status">
                Reviewing this run…
              </Text>
            ) : (
              <Stack direction="row" align="baseline" gap={2} wrap>
                <Text
                  render={<span />}
                  className="text-2xl leading-tight font-medium tabular-nums"
                >
                  {total.toLocaleString()}
                </Text>
                <Text render={<span />} variant="body" tone="muted">
                  {total === 1 ? "active finding" : "active findings"}
                </Text>
                {reviewing && (
                  <Text variant="caption" tone="muted" role="status">
                    Reviewing this run…
                  </Text>
                )}
              </Stack>
            )}
            {total === 0 ? (
              reviewing ? null : (
                (empty ?? (
                  <Text variant="body" tone="muted">
                    Nothing waiting for review.
                  </Text>
                ))
              )
            ) : (
              <Stack gap={2}>
                <ul className="flex flex-col gap-1">
                  {groups.slice(0, PREVIEW).map((group) => (
                    <li key={group.rule}>
                      <Link
                        to={groupPath(group.rule)}
                        className="-mx-1 flex min-h-11 items-center justify-between gap-3 rounded-md px-2 py-2.5 text-sm hover:bg-neutral-subtle-hovered focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                      >
                        <span className="min-w-0 text-ink">{group.rule}</span>
                        <span className="flex shrink-0 items-center gap-3 text-subtle tabular-nums">
                          {group.count.toLocaleString()}
                          <ArrowRightIcon aria-hidden className="size-3.5" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
      <CardFooter className="flex-wrap justify-between gap-2">
        {spec.work && (
          <Button nativeButton={false} render={<Link to={reviewPath} />}>
            Open review <ArrowRightIcon aria-hidden />
          </Button>
        )}
        {reviewAction}
      </CardFooter>
    </Card>
  );
};
